import i18n from '../../i18n';

/**
 * Catalogue de variables du menu `@` — mise à plat de la réponse de
 * `getVariables({ id, type })` (`src/requests/documentDatas.js`).
 *
 * La réponse a deux formes selon le document :
 *  - **template** : chaque groupe liste ses clés (`{ entity: ['NAME', ...] }`),
 *    aucune valeur ;
 *  - **document** : chaque groupe porte ses valeurs
 *    (`{ entity: { NAME: 'Agence Nord' } }`), éventuellement enveloppé dans
 *    `offer_variables` + `opportunities_variables` pour une offre.
 *
 * Les deux formes produisent le même catalogue : `{ byKey, groups }`, où une
 * clé canonique vaut `<groupe>.<CLÉ>` (`entity.NAME`) et une variable
 * personnalisée `custom.<id>`.
 */

/** Clés de la réponse API qui ne sont pas des groupes de variables. */
const RESERVED_ROOT_KEYS = new Set([
  'variable_groups',
  'custom_text_variables',
  'custom_text_variable_remarks',
  'offer_variables',
  'opportunities_variables',
]);

/** Accents et casse retirés — base de comparaison du filtre du menu `@`. */
export function normalizeForSearch(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function capitalize(value) {
  const str = String(value || '');
  return str ? str.charAt(0).toUpperCase() + str.slice(1) : str;
}

/** i18n renvoie la clé elle-même quand la traduction manque. */
function translate(key) {
  const translated = i18n.t(key);
  return translated && translated !== key ? translated : null;
}

/**
 * Libellé lisible d'un groupe, dérivé de la traduction de sa clé `NAME`
 * (« Nom de l'agence concernée » → « Agence concernée »), comme le fait
 * l'éditeur classique (`SelectionVariableTranslator`).
 */
export function groupLabelFromPrefix(prefix) {
  const raw = translate(`${prefix}.NAME`);
  if (!raw) return capitalize(String(prefix).replace(/_/g, ' '));
  return capitalize(
    raw.replace(/^Nom de l'|^Nom de la |^Nom du |^Nom de |^Nom /i, '').trim(),
  ) || capitalize(prefix);
}

function addVariable(acc, { key, group, name, value }) {
  if (acc.byKey[key]) return;
  acc.byKey[key] = { key, group, name, value: value ?? '' };
  if (!acc.groupKeys[group]) {
    acc.groupKeys[group] = [];
    acc.groupOrder.push(group);
  }
  acc.groupKeys[group].push(key);
}

/**
 * Ajoute un groupe de la réponse API.
 * `raw` est soit un tableau de clés (template), soit un objet clé → valeur.
 */
function collectGroup(acc, { prefix, raw, keyPrefix = '', labelPrefix = '' }) {
  const group = `${keyPrefix}${prefix}`;
  const label = labelPrefix
    ? `${labelPrefix} / ${groupLabelFromPrefix(prefix)}`
    : groupLabelFromPrefix(prefix);
  acc.groupLabels[group] = label;

  const add = (shortKey, value) => addVariable(acc, {
    key: `${keyPrefix}${prefix}.${shortKey}`,
    group,
    name: translate(`${prefix}.${shortKey}`) || shortKey,
    value: typeof value === 'string' ? value : '',
  });

  if (Array.isArray(raw)) {
    raw.forEach((shortKey) => add(shortKey, ''));
    return;
  }
  if (raw && typeof raw === 'object') {
    Object.entries(raw).forEach(([shortKey, value]) => add(shortKey, value));
  }
}

/** `custom_text_variables` : `{ 'Nom lisible.12': 'valeur' }` → `custom.12`. */
function collectCustomVariables(acc, customTextVariables) {
  if (!customTextVariables || typeof customTextVariables !== 'object') return;
  acc.groupLabels.custom = 'Variables personnalisées';
  Object.entries(customTextVariables).forEach(([nameWithId, value]) => {
    const cut = nameWithId.lastIndexOf('.');
    if (cut <= 0) return;
    addVariable(acc, {
      key: `custom.${nameWithId.slice(cut + 1)}`,
      group: 'custom',
      name: nameWithId.slice(0, cut),
      value: typeof value === 'string' ? value : '',
    });
  });
}

/**
 * Groupes déclarés par l'API (`variable_groups`) : ils portent un libellé
 * lisible et la liste des clés qu'ils rassemblent. Quand ils désignent des
 * clés connues, ils remplacent le découpage par préfixe pour ces clés-là.
 */
function applyApiGroups(acc, variableGroups) {
  if (!variableGroups || typeof variableGroups !== 'object') return;
  const entries = Array.isArray(variableGroups)
    ? variableGroups.map((g, i) => [String(g?.id ?? i), g])
    : Object.entries(variableGroups);

  entries.forEach(([id, group]) => {
    const label = group?.name || group?.label;
    const keys = group?.variable_ids || group?.variableIds || group?.keys;
    if (!label || !Array.isArray(keys)) return;
    const known = keys.filter((key) => acc.byKey[key]);
    if (!known.length) return;

    const groupId = `group:${id}`;
    acc.groupLabels[groupId] = label;
    acc.groupKeys[groupId] = known;
    acc.groupOrder.unshift(groupId);
    known.forEach((key) => {
      const previous = acc.byKey[key].group;
      acc.byKey[key] = { ...acc.byKey[key], group: groupId };
      acc.groupKeys[previous] = acc.groupKeys[previous].filter((k) => k !== key);
    });
  });
}

/**
 * Réponse brute de `getVariables` → catalogue exploitable par le menu `@`.
 *
 * @param {object|null|undefined} raw
 * @returns {{ byKey: object, groups: Array<{ id: string, label: string, keys: string[] }> }}
 */
export function buildVariableCatalog(raw) {
  const acc = {
    byKey: {}, groupKeys: {}, groupLabels: {}, groupOrder: [],
  };
  if (!raw || typeof raw !== 'object') return { byKey: {}, groups: [] };

  const base = raw.offer_variables && typeof raw.offer_variables === 'object'
    ? raw.offer_variables
    : raw;

  Object.entries(base).forEach(([prefix, value]) => {
    if (RESERVED_ROOT_KEYS.has(prefix)) return;
    collectGroup(acc, { prefix, raw: value });
  });

  // Offre : les opportunités liées portent leurs variables sous le préfixe de
  // leur numéro chrono (`D-7.entity.NAME`), comme dans l'éditeur classique.
  if (Array.isArray(raw.opportunities_variables)) {
    raw.opportunities_variables.forEach((entry) => {
      if (!entry || typeof entry !== 'object') return;
      const opportunityName = Object.keys(entry)[0];
      const groups = entry[opportunityName];
      const chrono = groups?.opportunity?.CHRONO;
      if (!chrono) return;
      Object.entries(groups).forEach(([prefix, value]) => {
        if (RESERVED_ROOT_KEYS.has(prefix)) return;
        collectGroup(acc, {
          prefix,
          raw: value,
          keyPrefix: `${chrono}.`,
          labelPrefix: opportunityName,
        });
      });
    });
  }

  collectCustomVariables(acc, raw.custom_text_variables);
  applyApiGroups(acc, raw.variable_groups);

  const groups = acc.groupOrder
    .map((id) => ({
      id,
      label: acc.groupLabels[id] || id,
      keys: (acc.groupKeys[id] || [])
        .slice()
        .sort((a, b) => acc.byKey[a].name.localeCompare(acc.byKey[b].name)),
    }))
    .filter((group) => group.keys.length > 0);

  return { byKey: acc.byKey, groups };
}

/** Catalogue → `{ clé: valeur }`, la forme attendue par le parser. */
export function variableValueMap(catalog) {
  const out = {};
  Object.values(catalog?.byKey || {}).forEach(({ key, value }) => {
    out[key] = value ?? '';
  });
  return out;
}

/**
 * Filtre le catalogue sur les mots tapés après le `@` : sans accents, **tous**
 * les mots doivent être présents dans le nom de la variable — même règle que
 * `SelectionVariableTranslator` dans l'éditeur classique.
 *
 * @returns {Array<{ id, label, items: Array<{ key, name, value }> }>} groupes non vides
 */
export function filterVariableCatalog(catalog, query = '') {
  const tokens = normalizeForSearch(query).split(/\s+/).filter(Boolean);
  const groups = catalog?.groups || [];
  const byKey = catalog?.byKey || {};

  return groups
    .map(({ id, label, keys }) => ({
      id,
      label,
      items: keys
        .map((key) => byKey[key])
        .filter((variable) => {
          if (!variable) return false;
          if (!tokens.length) return true;
          const name = normalizeForSearch(variable.name);
          return tokens.every((token) => name.includes(token));
        }),
    }))
    .filter((group) => group.items.length > 0);
}

/**
 * Liste à plat des variables filtrées, dans l'ordre d'affichage du menu, chaque
 * entrée portant son groupe : le menu redessine ses en-têtes à la volée et
 * l'index de sélection clavier reste un simple entier.
 */
export function flattenFilteredGroups(filteredGroups, { limit = Infinity } = {}) {
  const out = [];
  filteredGroups.forEach(({ id, label, items }) => {
    items.forEach((variable) => {
      if (out.length >= limit) return;
      out.push({ ...variable, groupId: id, groupLabel: label });
    });
  });
  return out;
}

/**
 * Catalogue → options groupées d'un `Select` de sidebar, dans l'ordre du menu
 * `@`. `keep(variable)` restreint la liste ; sans lui, tout est proposé.
 *
 * Le filtre ne peut porter que sur la **valeur** — aucune variable n'est typée
 * côté back. Un modèle de site sert justement son catalogue sans valeurs
 * (`variable_names_only`) : ses appelants n'y passent donc pas de `keep`, et la
 * valeur est écrite plus tard, à la génération du site du document.
 */
export function variableSelectOptions(catalog, keep) {
  return (catalog?.groups || [])
    .map((group) => ({
      group: group.label,
      items: (group.keys || [])
        .map((key) => catalog.byKey[key])
        .filter((variable) => variable && (!keep || keep(variable)))
        .map((variable) => ({ value: variable.key, label: variable.name })),
    }))
    .filter((group) => group.items.length > 0);
}
